import _ from 'lodash';
import i18n from 'i18next';
import { toastr } from 'react-redux-toastr';
import { getTaskDetails } from 'home/HomePage/actions/tasks.actions';
import { noteConstants, taskConstants } from 'home/HomePage/constants/constants';
import { alertActions } from '../../../alert';
import { axiosInstance, ENDPOINT } from '../../../common/constants';
import { handleResponse } from '../../../common/helpers';
import ActivityTypes from './activity.types';
import * as activityService from './activity.service';
import { buildActivityParams, continuationCursor } from '../helpers/activityParams';

import * as utils from '../../../common/utils';
import { isVibrationAgentEnabled } from '../../../common/rbac/util';

import * as definitionService from '../../Settings/EventDefinitions/definition.service';
import definitionTypes from '../../Settings/EventDefinitions/definition.types';
import { violatedFeature } from '../helpers/overallAlarmActivity';

export const getActivity = (
  searchParams,
  machine_id,
  paginate = false,
  newCtx = false,
  storeInRedux = true,
  eventCallbacks = {
    onSuccess: () => {},
    onFailure: () => {}
  }
) => {
  const request = newContext => ({ type: ActivityTypes.GET_EVENTS_REQUEST, newContext });
  const success = events => ({ type: ActivityTypes.GET_EVENTS_SUCCESS, events });
  const failure = (error, paginated) => ({ type: ActivityTypes.GET_EVENTS_FAILURE, error, paginated });

  return (dispatch, getState) => {
    const prevState = getState().events;
    const account_id = getState().user.user.account_id;
    let { nextPage } = prevState;
    // newContext will reset current state stored list of events as well as reset pageNr to 1
    let newContext = !_.isEqual(searchParams, prevState.searchParams) || (machine_id !== prevState.machine_id) || (account_id !== prevState.account_id) || newCtx;
    if (paginate) newContext = false;
    if (newContext) {
      nextPage = 1;
    }
    const cursor = continuationCursor(prevState, searchParams, paginate, newContext);
    const params = buildActivityParams(searchParams, {
      machine_id,
      page: nextPage,
      per_page: searchParams.per_page || ActivityTypes.NUMBER_OF_EVENTS_PER_PAGE
    });
    if (cursor) params.cursor = cursor;

    if (storeInRedux) {
      dispatch(request(newContext));
      dispatch(alertActions.clear());
    }
    return handleResponse(axiosInstance.get(ENDPOINT.GET_ACTIVITY, {
      params
    }))
      .then(
        (res) => {
          // A cursor page carries no totals: the first page's stand. Its end is
          // the absence of a next cursor.
          const hasMore = cursor ? Boolean(res.next_cursor) : res.total_pages > nextPage;
          const total_count = cursor ? prevState.total_count : res.total_count;
          const newList = res.items.map(activity => ({
            ...activity,
            timestamp: activity.timestamp
          }));
          let list = [];
          let observation_list = [];
          if (nextPage === 1) {
            newList.forEach((activity) => {
              if (!activity.observation) list.push(activity);
              else observation_list.push(activity);
            });
          } else {
            list = [...prevState.list, ...newList];
            observation_list = [...prevState.observation_list];
          }
          const events = {
            account_id,
            machine_id,
            list,
            observation_list,
            hasMore,
            searchParams,
            nextPage: hasMore ? (nextPage + 1) : nextPage,
            nextCursor: res.next_cursor || null,
            total_count
          };
          if (eventCallbacks && eventCallbacks.onSuccess) eventCallbacks.onSuccess(events);
          if (storeInRedux) dispatch(success(events));
          return res.items;
        },
        (error) => {
          if (eventCallbacks && eventCallbacks.onFailure) eventCallbacks.onFailure(error);
          if (storeInRedux) {
            dispatch(failure(error, nextPage > 1));
            dispatch(alertActions.error(error.message));
          }
        }
      );
  };
};

// Names each grouped request, so the reducer can tell the response it is waiting
// for from one asked under filters the user has since changed.
let groupsRequestSeq = 0;
let groupActivitiesRequestSeq = 0;

// Grouped ("Group by Asset") view.
//
// This used to request per_page: 500 and then recursively dispatch itself for
// every remaining page until the whole filtered activity set had been downloaded,
// pooling each batch into Redux and grouping client-side. That only ever worked
// because the view was pinned to open events, whose count is bounded by the number
// of event definitions. It now asks for one page of MACHINE groups: the backend
// returns per-machine aggregates and no activity rows, so a page costs the same
// whatever the filters select.
export const getGroupedActivity = (searchParams, machine_id, storeInRedux = true, page = 1, paginate = false, newCtx = false) => {
  const request = (newContext, requestId) => ({ type: ActivityTypes.GET_GROUPS_REQUEST, newContext, requestId });
  const success = (groups, requestId) => ({ type: ActivityTypes.GET_GROUPS_SUCCESS, groups, requestId });
  const failure = (error, requestId, page) => ({ type: ActivityTypes.GET_GROUPS_FAILURE, error, requestId, page });

  return (dispatch, getState) => {
    const prevState = getState().events;
    const account_id = getState().user.user.account_id;

    // `newCtx` is the caller saying "start over" for a reason the search params
    // cannot show: after deleting an activity or marking one read, the params are
    // unchanged, so without it the refresh appended the NEXT page of groups to
    // the stale ones instead of reloading the first.
    let newContext = !_.isEqual(searchParams, prevState.searchParams)
      || (machine_id !== prevState.machine_id)
      || (account_id !== prevState.account_id)
      || newCtx;
    if (paginate) newContext = false;

    const nextPage = newContext ? 1 : page;

    // Groups have their own page size. A per_page persisted from the individual
    // view is a number of activities, not of machines.
    const params = buildActivityParams(searchParams, {
      machine_id,
      page: nextPage,
      per_page: ActivityTypes.NUMBER_OF_GROUPS_PER_PAGE
    });

    groupsRequestSeq += 1;
    const requestId = groupsRequestSeq;
    if (storeInRedux) {
      dispatch(request(newContext, requestId));
      dispatch(alertActions.clear());
    }

    return activityService.getActivityGroups(params)
      .then(
        (res) => {
          const hasMore = res.total_pages > nextPage;
          const newGroups = res.groups || [];
          // The reducer appends a later page to the groups it holds when the
          // response lands, not to the ones captured here, so a mark-read or a
          // delete made while the page was loading is kept.
          if (storeInRedux) {
            dispatch(success({
              account_id,
              machine_id,
              groups: newGroups,
              page: nextPage,
              hasMore,
              searchParams,
              nextPage: hasMore ? (nextPage + 1) : nextPage,
              total_count: res.total_count
            }, requestId));
          }
          return newGroups;
        },
        (error) => {
          if (storeInRedux) {
            dispatch(failure(error, requestId, nextPage));
            dispatch(alertActions.error(error.message));
          }
        }
      );
  };
};

// Activities for one machine, fetched when the user expands its group. Bounded and
// paginated: the group header already shows the true total, so "Show more" knows
// exactly how many rows remain.
export const getGroupActivities = (searchParams, machineId, page = 1) => {
  const request = payload => ({ type: ActivityTypes.GET_GROUP_ACTIVITIES_REQUEST, payload });
  const success = payload => ({ type: ActivityTypes.GET_GROUP_ACTIVITIES_SUCCESS, payload });
  const failure = payload => ({ type: ActivityTypes.GET_GROUP_ACTIVITIES_FAILURE, payload });

  return (dispatch) => {
    const params = buildActivityParams(searchParams, {
      machine_id: machineId,
      page,
      per_page: ActivityTypes.NUMBER_OF_GROUP_ACTIVITIES_PER_PAGE
    });
    groupActivitiesRequestSeq += 1;
    const requestId = groupActivitiesRequestSeq;
    dispatch(request({ machineId, page, requestId }));

    return activityService.getGroupActivities(params)
      .then(
        (res) => {
          dispatch(success({
            machineId,
            requestId,
            page,
            items: res.items || [],
            total_count: res.total_count,
            hasMore: res.total_pages > page
          }));
          return res.items;
        },
        (error) => {
          dispatch(failure({ machineId, requestId, error }));
          dispatch(alertActions.error(error.message));
        }
      );
  };
};

export const getEventDetails = (id, activity_type) => {
  const request = () => ({ type: ActivityTypes.GET_EVENT_DETAILS_REQUEST, id, activity_type });
  const success = event => ({ type: ActivityTypes.GET_EVENT_DETAILS_SUCCESS, event });
  const failure = error => ({ type: ActivityTypes.GET_EVENT_DETAILS_FAILURE, error });

  return (dispatch) => {
    dispatch(request());
    // todo add view_all option
    return handleResponse(axiosInstance.get(ENDPOINT.GET_EVENT_DETAILS(id), { params: { view_all: false } }))
      .then(
        (res) => {
          const details = {
            ...res,
            description_metadata: _.mapValues(res.description_metadata, (metadata) => {
              if (utils.isValidDatetime(metadata)) return utils.formatDate(metadata);
              return metadata;
            })
          };
          dispatch(success({ details, id, activity_type }));
        },
        (error) => {
          dispatch(failure({ error, id, activity_type }));
        }
      );
  };
};

export const getComments = (activity_id, activity_type) => {
  const request = () => ({ type: ActivityTypes.GET_ACTIVITY_COMMENTS_REQUEST, activity_type, activity_id });
  const success = item => ({ type: ActivityTypes.GET_ACTIVITY_COMMENTS_SUCCESS, item });
  const failure = error => ({ type: ActivityTypes.GET_ACTIVITY_COMMENTS_FAILURE, error });

  return (dispatch) => {
    dispatch(request());
    return handleResponse(axiosInstance.get(ENDPOINT.GET_ACTIVITY_COMMENTS(activity_type, activity_id)))
      .then(
        (details) => {
          dispatch(success({ details, activity_id, activity_type }));
        },
        (error) => {
          dispatch(failure({ error, activity_id, activity_type }));
        }
      );
  };
};

export const getNoteDetails = (id, activity_type, isTaskPage = false, isInternal = false, note = null) => {
  const setOpenNoteDetails = noteDetails => ({ type: noteConstants.SET_NOTE_DETAILS, noteDetails });
  const updateTaskNote = note => ({ type: taskConstants.UPDATE_TASK_NOTE, note });
  const request = () => ({ type: ActivityTypes.GET_NOTE_DETAILS_REQUEST, id, activity_type });
  const success = note => ({ type: ActivityTypes.GET_NOTE_DETAILS_SUCCESS, note });
  const failure = error => ({ type: ActivityTypes.GET_NOTE_DETAILS_FAILURE, error });

  return (dispatch, getState) => {
    if (isInternal) {
      const noteDetails = {
        ...note,
        id: note.activity_id,
        activity_type: note.type,
        details: {
          note: note.note_text
        }
      };
      dispatch(success(noteDetails));
      return noteDetails;
    }

    dispatch(request());
    return handleResponse(axiosInstance.get(ENDPOINT.GET_NOTE_DETAILS(id)))
      .then(
        (res) => {
          const details = {
            ...res
          };
          switch (details.chart_snapshot_data && details.chart_snapshot_data.chart_type) {
            case 'trend':
              details.chart_snapshot_data = {
                ...details.chart_snapshot_data,
                data: {
                  ...res.chart_snapshot_data.data,
                  chart_data: utils.formatChartData(
                    details.chart_snapshot_data.data.data,
                    utils.convertTimestringsToDateObjects(details.chart_snapshot_data.data.time),
                    null,
                    { measurement_id: details.chart_snapshot_data.data.measurement_id }
                  )
                }
              };
              break;
            case 'spectrum':
              details.chart_snapshot_data = {
                ...details.chart_snapshot_data,
                data: {
                  ...details.chart_snapshot_data.data,
                  chart_data: utils.formatChartData(
                    details.chart_snapshot_data.data.mag,
                    details.chart_snapshot_data.data.freq
                  )
                }
              };
              break;
            case 'waveform':
              details.chart_snapshot_data = {
                ...details.chart_snapshot_data,
                data: {
                  ...details.chart_snapshot_data.data,
                  chart_data: utils.formatChartData(
                    details.chart_snapshot_data.data.data,
                    details.chart_snapshot_data.data.time
                  )
                }
              };
              break;
            default:
              break;
          }
          dispatch(success({ details, id, activity_type }));

          const noteModal = getState().noteModal;
          if (noteModal.open && noteModal.noteId === id) {
            dispatch(setOpenNoteDetails({ ...res, note_text: res.note }));
          }
          if (isTaskPage) {
            dispatch(updateTaskNote({ ...res, note_text: res.note }));
          }

          return res;
        },
        (error) => {
          dispatch(failure({ error, id, activity_type }));
          toastr.error(error.message);
          throw error;
        }
      );
  };
};

export const postComment = (id, comment, type) => {
  const request = () => ({ type: ActivityTypes.POST_ACTIVITY_COMMENT_REQUEST });
  const success = item => ({ type: ActivityTypes.POST_ACTIVITY_COMMENT_SUCCESS, item });
  const failure = error => ({ type: ActivityTypes.POST_ACTIVITY_COMMENT_FAILURE, error });

  return (dispatch) => {
    dispatch(request());
    dispatch(alertActions.clear());
    return handleResponse(axiosInstance.post(ENDPOINT.POST_COMMENT(type, id), { comment }))
      .then(
        (res) => {
          dispatch(success({ id, type, res }));
        },
        (error) => {
          dispatch(failure({ error, id, type }));
          dispatch(alertActions.error(error.message));
          toastr.error(error.message);
        }
      );
  };
};

export const putComment = (id, comment, type, comment_id) => {
  const request = () => ({ type: ActivityTypes.PUT_ACTIVITY_COMMENT_REQUEST });
  const success = item => ({ type: ActivityTypes.PUT_ACTIVITY_COMMENT_SUCCESS, item });
  const failure = error => ({ type: ActivityTypes.PUT_ACTIVITY_COMMENT_FAILURE, error });

  return (dispatch) => {
    dispatch(request());
    return handleResponse(axiosInstance.put(ENDPOINT.UPDATE_COMMENT(type, id, comment_id), { comment }))
      .then(
        (res) => {
          dispatch(success({ id, type, res }));
        },
        (error) => {
          dispatch(failure({ error, id, type }));
          dispatch(alertActions.error(error.message));
        }
      );
  };
};

export const deleteComment = (id, type, comment_id) => {
  const request = () => ({ type: ActivityTypes.DELETE_ACTIVITY_COMMENT_REQUEST });
  const success = item => ({ type: ActivityTypes.DELETE_ACTIVITY_COMMENT_SUCCESS, item });
  const failure = error => ({ type: ActivityTypes.DELETE_ACTIVITY_COMMENT_FAILURE, error });

  return (dispatch) => {
    dispatch(request());
    return handleResponse(axiosInstance.delete(ENDPOINT.DELETE_COMMENT(type, id, comment_id)))
      .then(
        (res) => {
          dispatch(success({ id, type, res }));
        },
        (error) => {
          dispatch(failure({ error, id, type }));
        }
      );
  };
};

export const deleteNote = (note_id, task_id = null) => {
  const request = () => ({ type: ActivityTypes.DELETE_NOTE_REQUEST });
  const success = () => ({ type: ActivityTypes.DELETE_NOTE_SUCCESS, note_id });
  const failure = error => ({ type: ActivityTypes.DELETE_NOTE_FAILURE, error });

  return (dispatch) => {
    dispatch(request());
    return handleResponse(axiosInstance.delete(ENDPOINT.NOTE(note_id)))
      .then(
        () => {
          dispatch(success());
          if (task_id) dispatch(getTaskDetails(task_id));
          toastr.success(i18n.t('activity.toastr.noteDeletedSuccess', { defaultValue: 'Note deleted successfully' }));
        },
        (error) => {
          dispatch(failure(error));
        }
      );
  };
};
/*
todo close event
export const closeEvent = (event_id) => {
  const request = () => ({ type: ActivityTypes.CLOSE_EVENT_REQUEST });
  const success = eventId => ({ type: ActivityTypes.CLOSE_EVENT_SUCCESS, eventId });
  // const failure = error => ({ type: ActivityTypes.CLOSE_EVENT_FAILURE, error });

  const mockCloseParams = {
    status: 'inactive',
    close_reason: 'this is why i close this'
  };

  return (dispatch) => {
    dispatch(request());
    dispatch(alertActions.clear());
    dispatch(success(event_id));
    // return handleResponse(axiosInstance.put(ENDPOINT.CLOSE_EVENT(event_id), {
    //   mockCloseParams
    // }))
    //   .then(
    //     (res) => {
    //       dispatch(success(res));
    //     },
    //     (error) => {
    //       dispatch(failure(error));
    //       dispatch(alertActions.error(error));
    //     }
    //   );
  };
};
*/
export const getResourcesBasedOnType = (resource_type) => {
  return (dispatch) => {
    const params = {
      resource_type
    };

    dispatch(request());
    return definitionService.getResources(params).then(
      (res) => {
        const resources = res.result.map(r => ({ value: r.id, text: r.name }));
        dispatch(success(resources));
        return resources;
      },
      (error) => {
        dispatch(failure(error));
      }
    );
  };

  function request() {
    return { type: definitionTypes.GET_RESOURCES_REQUEST };
  }
  function success(resources) {
    return { type: definitionTypes.GET_RESOURCES_SUCCESS, resources };
  }
  function failure(error) {
    return { type: definitionTypes.GET_RESOURCES_FAILURE, error };
  }
};

export const updateActivity = (payload, action, time_period = null, comment = null) => {
  const request = () => ({ type: ActivityTypes.UPDATE_ACTIVITY_REQUEST });
  const success = (message, payload, action) => ({ type: ActivityTypes.UPDATE_ACTIVITY_SUCCESS, message, payload, action });
  const failure = error => ({ type: ActivityTypes.UPDATE_ACTIVITY_FAILURE, error });

  return (dispatch) => {
    const params = {
      payload,
      action,
      ...(time_period && { time_period }),
      ...(comment && { comment })
    };

    dispatch(request());
    return handleResponse(axiosInstance.patch(ENDPOINT.UPDATE_ACTIVITY, params))
      .then(
        (res) => {
          dispatch(success(res.message, payload, action));
        },
        (error) => {
          dispatch(failure(error));
        }
      );
  };
};

export const markEventsAsAnalyzed = (event_ids, notes, machine_id) => {
  const request = () => ({ type: ActivityTypes.MARK_EVENTS_ANALYZED_REQUEST });
  const success = () => ({ type: ActivityTypes.MARK_EVENTS_ANALYZED_SUCCESS });
  const failure = () => ({ type: ActivityTypes.MARK_EVENTS_ANALYZED_SUCCESS });

  return (dispatch) => {
    const params = {
      event_ids,
      notes,
      machine_id
    };

    dispatch(request());
    return handleResponse(axiosInstance.post(ENDPOINT.MARK_EVENTS_AS_ANALYZED, params))
      .then(
        (res) => {
          dispatch(success());
          toastr.success(res.message);
        },
        (error) => {
          dispatch(failure());
          toastr.error(error.message);
        }
      );
  };
};

export const updateActivityRead = (payload, action) => {
  const request = () => ({ type: ActivityTypes.UPDATE_ACTIVITY_READ_REQUEST });
  const success = (message, payload, action) => ({ type: ActivityTypes.UPDATE_ACTIVITY_READ_SUCCESS, message, payload, action });
  const failure = error => ({ type: ActivityTypes.UPDATE_ACTIVITY_READ_FAILURE, error });

  return (dispatch) => {
    const params = {
      payload,
      action
    };

    dispatch(request());
    return handleResponse(axiosInstance.patch(ENDPOINT.UPDATE_ACTIVITY_READ, params))
      .then(
        (res) => {
          dispatch(success(res.message, payload, action));
        },
        (error) => {
          dispatch(failure(error));
        }
      );
  };
};

// Update a single activity's AI analysis status in Redux store
export const updateActivityAIStatus = (activityId, aiAnalysisStatus, aiAnalysis = null) => ({
  type: ActivityTypes.UPDATE_ACTIVITY_AI_STATUS,
  payload: {
    activityId,
    aiAnalysisStatus,
    aiAnalysis
  }
});

// AI Vibration Analysis Actions
export const triggerVibrationAnalysis = eventData => (dispatch, getState) => {
  const user = getState().user.user;
  const currentAccount = getState().currentAccount;

  // Frontend check (backend also validates for security)
  if (!isVibrationAgentEnabled(user, currentAccount)) {
    toastr.error(i18n.t('activity.toastr.vibrationAgentDisabled', { defaultValue: 'Vibration agent is not enabled for your account' }));
    return Promise.reject(new Error('Feature not enabled'));
  }


  const params = {
    event_id: String(eventData.activity_id),
    machine_id: String(eventData.machine_id),
    location_id: String(eventData.location_id),
    tag_id: String(eventData.tag_id),
    timestamp: eventData.timestamp,
    alarm_type: eventData.event_type || 'vibration_critical',
    severity: eventData.severity || 'high',
    analysis_types: ['trend', 'spectrum'],
    spectrum_mode: 'order_normalized',
    violated_feature: violatedFeature(eventData),
    ...(eventData.parent_analysis_id && { parent_analysis_id: eventData.parent_analysis_id })
  };
  // Immediately update the status to 'pending' in Redux
  dispatch(updateActivityAIStatus(eventData.activity_id, 'pending'));

  return handleResponse(axiosInstance.post(ENDPOINT.TRIGGER_VIBRATION_ANALYSIS, params))
    .then(
      (res) => {
        toastr.success(i18n.t('activity.toastr.aiAnalysisTriggered', { defaultValue: 'AI Analysis triggered successfully' }));
        return res;
      },
      (error) => {
        // Revert to 'not-analyzed' if the trigger fails
        dispatch(updateActivityAIStatus(eventData.activity_id, 'failed'));
        toastr.error(error.message || i18n.t('activity.toastr.aiAnalysisTriggerFailed', { defaultValue: 'Failed to trigger AI analysis' }));
        throw error;
      }
    );
};

/**
 * Redo vibration analysis: run the full pipeline again for the same event (manual trigger with event context).
 * Use for v1 analyses that cannot use Regenerate. Reuses existing trigger endpoint.
 * Passes current analysis ID as parent_analysis_id so the new analysis is linked in the history chain.
 */
export const redoVibrationAnalysis = event => (dispatch) => {
  const eventWithParent = {
    ...event,
    parent_analysis_id: event.ai_analysis ? event.ai_analysis.analysis_id : null
  };
  return dispatch(triggerVibrationAnalysis(eventWithParent));
};

export const getVibrationAnalysis = analysisId => (
  () => (
    handleResponse(axiosInstance.get(ENDPOINT.GET_VIBRATION_ANALYSIS(analysisId)))
      .then(
        res => res,
        (error) => {
          toastr.error(error.message || i18n.t('activity.toastr.analysisLoadFailed', { defaultValue: 'Failed to get analysis' }));
          throw error;
        }
      )
  )
);

export const getEventVibrationAnalyses = eventId => (
  () => (
    handleResponse(axiosInstance.get(ENDPOINT.GET_EVENT_VIBRATION_ANALYSES(eventId)))
      .then(
        res => res,
        () => ({ analyses: [], count: 0, event_id: eventId }) // Silently handle error for polling
      )
  )
);

// Poll AI analysis results for pending events
export const pollPendingAnalyses = pendingEventIds => (dispatch) => {
  if (!pendingEventIds || pendingEventIds.length === 0) {
    return Promise.resolve();
  }

  // Fetch analysis status for each pending event
  const promises = pendingEventIds.map(eventId => handleResponse(axiosInstance.get(ENDPOINT.GET_EVENT_VIBRATION_ANALYSES(eventId)))
    .then(
      (res) => {
        // Get the latest analysis for this event
        // API returns { analyses: [], count: N, event_id: X }
        if (res.analyses && res.analyses.length > 0) {
          const latestAnalysis = res.analyses[0]; // First item is the latest

          // Only update if status has changed from pending
          if (latestAnalysis.status === 'completed') {
            // Map backend status to frontend format and dispatch update
            dispatch(updateActivityAIStatus(
              eventId,
              'completed',
              {
                analysis_id: latestAnalysis.id, // Include analysis_id for feedback operations
                overall_assessment: latestAnalysis.overall_assessment,
                supporting_evidence: latestAnalysis.supporting_evidence || [], // Include supporting evidence
                risk_level: latestAnalysis.risk_level,
                urgency: latestAnalysis.urgency,
                suspected_defect: latestAnalysis.suspected_defect,
                suspected_defects: latestAnalysis.suspected_defects,
                recommendations: latestAnalysis.recommendations || [],
                confidence_score: latestAnalysis.confidence_score,
                langsmith_trace_url: latestAnalysis.langsmith_trace_url, // Include trace URL for debugging
                parent_analysis_id: latestAnalysis.parent_analysis_id, // Include parent ID for regeneration detection
                analysis_version: latestAnalysis.analysis_version || 1, // v1=legacy, v2=can regenerate
                // Sub-agent results for detailed spectrum and trend views
                spectrum_sub_agent: latestAnalysis.spectrum_sub_agent || null,
                trend_sub_agent: latestAnalysis.trend_sub_agent || null,
                // Triaxial (multi-direction) analysis: overview + per-direction sub-agents
                scope: latestAnalysis.scope,
                directions_analyzed: latestAnalysis.directions_analyzed,
                capture_timestamp: latestAnalysis.capture_timestamp,
                per_direction_findings: latestAnalysis.per_direction_findings || null,
                // Degradation + provenance so a polled refresh keeps the same shape
                // as the initial load (this hand-written allowlist drops any field
                // not listed here).
                degraded_reason: latestAnalysis.degraded_reason,
                model_name: latestAnalysis.model_name,
                // Location analysis: which flow made it, the alarms it covers,
                // a note per direction and the evidence tables.
                pipeline: latestAnalysis.pipeline,
                alarms: latestAnalysis.alarms || null,
                violated_features: latestAnalysis.violated_features || null,
                covered_directions: latestAnalysis.covered_directions || null,
                direction_notes: latestAnalysis.direction_notes || null,
                evidence_sheet: latestAnalysis.evidence_sheet || null,
                // The code check's record, for the callout under the risk badge.
                guardrail: latestAnalysis.guardrail || null
              }
            ));
          } else if (latestAnalysis.status === 'failed') {
            dispatch(updateActivityAIStatus(eventId, 'failed'));
          }
          // If still pending, do nothing
        }
        return { eventId, status: 'checked' };
      },
      () => ({ eventId, status: 'error' }) // Silently handle errors for individual events
    ));

  return Promise.all(promises);
};

// ============================================================================
// Vibration Analysis Feedback Actions
// ============================================================================

/**
 * Create comprehensive analyst feedback for an AI analysis
 * @param {number} analysisId - The analysis ID to provide feedback for
 * @param {object} feedbackData - Feedback data containing:
 *   - feedback_type: 'validation' | 'correction' | 'enhancement' | 'rejection'
 *   - rating: 1-5 (optional)
 *   - is_accurate: boolean (optional)
 *   - corrected_overall_assessment: string (optional)
 *   - corrected_risk_level: string (optional)
 *   - corrected_urgency: string (optional)
 *   - corrected_suspected_defect: string (optional)
 *   - corrected_recommendations: string[] (optional)
 *   - analyst_notes: string (optional)
 *   - fields_corrected: string[] (optional)
 */
export const createVibrationAnalysisFeedback = (analysisId, feedbackData) => () =>
  handleResponse(axiosInstance.post(ENDPOINT.CREATE_VIBRATION_ANALYSIS_FEEDBACK(analysisId), feedbackData))
    .then(
      (res) => {
        toastr.success(i18n.t('activity.toastr.feedbackSubmitted', { defaultValue: 'Feedback submitted successfully' }));
        return res;
      },
      (error) => {
        toastr.error(error.message || i18n.t('activity.toastr.feedbackSubmitFailed', { defaultValue: 'Failed to submit feedback' }));
        throw error;
      }
    );

/**
 * Get all feedback for a specific analysis
 * @param {number} analysisId - The analysis ID to get feedback for
 */
export const getVibrationAnalysisFeedbacks = analysisId => () =>
  handleResponse(axiosInstance.get(ENDPOINT.GET_VIBRATION_ANALYSIS_FEEDBACKS(analysisId)))
    .then(
      res => res,
      (error) => {
        toastr.error(error.message || i18n.t('activity.toastr.feedbackLoadFailed', { defaultValue: 'Failed to get feedback' }));
        throw error;
      }
    );

/**
 * Get a specific feedback entry
 * @param {number} analysisId - The analysis ID
 * @param {number} feedbackId - The feedback ID
 */
export const getVibrationAnalysisFeedback = (analysisId, feedbackId) => () =>
  handleResponse(axiosInstance.get(ENDPOINT.GET_VIBRATION_ANALYSIS_FEEDBACK(analysisId, feedbackId)))
    .then(
      res => res,
      (error) => {
        toastr.error(error.message || i18n.t('activity.toastr.feedbackLoadFailed', { defaultValue: 'Failed to get feedback' }));
        throw error;
      }
    );

/**
 * Update existing feedback
 * @param {number} analysisId - The analysis ID
 * @param {number} feedbackId - The feedback ID to update
 * @param {object} feedbackData - Updated feedback data
 */
export const updateVibrationAnalysisFeedback = (analysisId, feedbackId, feedbackData) => () =>
  handleResponse(axiosInstance.patch(ENDPOINT.UPDATE_VIBRATION_ANALYSIS_FEEDBACK(analysisId, feedbackId), feedbackData))
    .then(
      (res) => {
        toastr.success(i18n.t('activity.toastr.feedbackUpdated', { defaultValue: 'Feedback updated successfully' }));
        return res;
      },
      (error) => {
        toastr.error(error.message || i18n.t('activity.toastr.feedbackUpdateFailed', { defaultValue: 'Failed to update feedback' }));
        throw error;
      }
    );

/**
 * Delete feedback
 * @param {number} analysisId - The analysis ID
 * @param {number} feedbackId - The feedback ID to delete
 */
export const deleteVibrationAnalysisFeedback = (analysisId, feedbackId) => () =>
  handleResponse(axiosInstance.delete(ENDPOINT.DELETE_VIBRATION_ANALYSIS_FEEDBACK(analysisId, feedbackId)))
    .then(
      (res) => {
        toastr.success(i18n.t('activity.toastr.feedbackDeleted', { defaultValue: 'Feedback deleted successfully' }));
        return res;
      },
      (error) => {
        toastr.error(error.message || i18n.t('activity.toastr.feedbackDeleteFailed', { defaultValue: 'Failed to delete feedback' }));
        throw error;
      }
    );

/**
 * Get vibration analysis by integer ID (for fetching parent analysis)
 * @param {number} analysisId - The integer analysis ID
 */
export const getVibrationAnalysisByIntId = analysisId => () =>
  handleResponse(axiosInstance.get(ENDPOINT.GET_VIBRATION_ANALYSIS_BY_INT_ID(analysisId)))
    .then(
      res => res,
      (error) => {
        // Silent error - used for optional parent analysis fetch
        throw error;
      }
    );

/**
 * Regenerate vibration analysis with feedback incorporated
 * @param {number} analysisId - The analysis ID to regenerate from
 * @param {Array<number>} feedbackIds - Array of feedback IDs to incorporate
 */
export const regenerateVibrationAnalysis = (analysisId, feedbackIds) => () =>
  handleResponse(axiosInstance.post(ENDPOINT.REGENERATE_VIBRATION_ANALYSIS(analysisId), {
    feedback_ids: feedbackIds
  }))
    .then(
      (res) => {
        toastr.success(i18n.t('activity.toastr.analysisRegenerationStarted', {
          defaultValue: 'Analysis regeneration started. This will take a few seconds...'
        }));
        return res;
      },
      (error) => {
        toastr.error(error.message || i18n.t('activity.toastr.analysisRegenerationFailed', {
          defaultValue: 'Failed to regenerate analysis'
        }));
        throw error;
      }
    );
